(() => {
  const POUNDS_PER_KILOGRAM = 2.20462;
  const STORAGE_KEY = 'littleNestPregnancyWeightResults';
  const MAX_SAVED_RESULTS = 10;
  const FIRST_TRIMESTER_GAIN = Object.freeze({ min: 0, max: 4.4 });
  const END_OF_FIRST_TRIMESTER_GAIN = Object.freeze({ min: 1.1, max: 4.4 });

  const weightGainGuidelines = {
    single: {
      underweight: { total: { min: 28, max: 40 }, weekly: { min: 1.0, max: 1.3 } },
      normal: { total: { min: 25, max: 35 }, weekly: { min: 0.8, max: 1.0 } },
      overweight: { total: { min: 15, max: 25 }, weekly: { min: 0.5, max: 0.7 } },
      obese: { total: { min: 11, max: 20 }, weekly: { min: 0.4, max: 0.6 } },
    },
    twins: {
      underweight: { total: { min: 50, max: 62 }, weekly: { min: 1.5, max: 1.8 } },
      normal: { total: { min: 37, max: 54 }, weekly: { min: 1.1, max: 1.5 } },
      overweight: { total: { min: 31, max: 50 }, weekly: { min: 0.9, max: 1.3 } },
      obese: { total: { min: 25, max: 42 }, weekly: { min: 0.7, max: 1.1 } },
    },
    triplets: {
      all: { total: { min: 50, max: 100 }, weekly: { min: 1.5, max: 2.0 } },
    },
  };

  const tipsByCategory = {
    underweight: [
      'Focus on nutrient-dense foods rich in protein and healthy fats',
      'Consider eating smaller meals more frequently throughout the day',
      'Include healthy snacks between meals',
      'Consult with your healthcare provider about additional nutritional supplements',
    ],
    normal: [
      'Maintain a balanced diet with plenty of fruits and vegetables',
      'Include lean proteins and whole grains in your meals',
      'Stay hydrated with water throughout the day',
      'Continue regular, approved exercise as recommended by your healthcare provider',
    ],
    overweight: [
      'Focus on quality nutrition rather than quantity',
      'Choose nutrient-dense foods over empty calories',
      'Include regular, approved physical activity',
      'Monitor weight gain closely with your healthcare provider',
    ],
    obese: [
      'Work closely with your healthcare provider to monitor weight gain',
      'Focus on highly nutritious foods',
      'Consider consulting with a registered dietitian',
      'Maintain approved physical activity as recommended by your doctor',
    ],
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (!copied) throw new Error('Copy failed');
  };

  const saveLocally = (record) => {
    let savedResults = [];

    try {
      savedResults = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(savedResults)) savedResults = [];
    } catch (error) {
      savedResults = [];
    }

    savedResults.unshift(record);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedResults.slice(0, MAX_SAVED_RESULTS)));
  };

  const getBmiCategory = (bmi) => {
    if (bmi < 18.5) return 'underweight';
    if (bmi < 25) return 'normal';
    if (bmi < 30) return 'overweight';
    return 'obese';
  };

  const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

  const initCalculator = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const form = root.querySelector('[data-btwg-form]');
    const unitInput = root.querySelector('[data-btwg-unit]');
    const preWeightInput = root.querySelector('[data-btwg-pre-weight]');
    const currentWeightInput = root.querySelector('[data-btwg-current-weight]');
    const weightUnit = root.querySelector('[data-btwg-weight-unit]');
    const currentWeightUnit = root.querySelector('[data-btwg-current-weight-unit]');
    const metricHeight = root.querySelector('[data-btwg-height-metric]');
    const imperialHeight = root.querySelector('[data-btwg-height-imperial]');
    const heightCmInput = root.querySelector('[data-btwg-height-cm]');
    const heightFeetInput = root.querySelector('[data-btwg-height-feet]');
    const heightInchesInput = root.querySelector('[data-btwg-height-inches]');
    const weekInput = root.querySelector('[data-btwg-week]');
    const pregnancyTypeInput = root.querySelector('[data-btwg-pregnancy-type]');
    const results = root.querySelector('[data-btwg-results]');
    const bmiElement = root.querySelector('[data-btwg-bmi]');
    const categoryElement = root.querySelector('[data-btwg-category]');
    const totalRangeElement = root.querySelector('[data-btwg-total-range]');
    const actualGainElement = root.querySelector('[data-btwg-actual-gain]');
    const statusElement = root.querySelector('[data-btwg-status]');
    const weeklyRangeElement = root.querySelector('[data-btwg-weekly-range]');
    const resultWeekElement = root.querySelector('[data-btwg-result-week]');
    const expectedRangeElement = root.querySelector('[data-btwg-expected-range]');
    const progressBar = root.querySelector('[data-btwg-progress-bar]');
    const progressFill = root.querySelector('[data-btwg-progress-fill]');
    const minWeightElement = root.querySelector('[data-btwg-min-weight]');
    const maxWeightElement = root.querySelector('[data-btwg-max-weight]');
    const tipsList = root.querySelector('[data-btwg-tips]');
    const saveForm = root.querySelector('[data-btwg-save-form]');
    const emailInput = root.querySelector('[data-btwg-email]');
    const saveStatus = root.querySelector('[data-btwg-save-status]');
    const resetButton = root.querySelector('[data-btwg-reset]');
    const copyButton = root.querySelector('[data-btwg-copy-code]');

    let currentUnit = 'imperial';
    let latestResult = null;

    const errorElement = (name) => root.querySelector(`[data-btwg-error="${name}"]`);
    const setError = (name, message) => {
      errorElement(name).textContent = message;
    };
    const clearErrors = () => {
      root.querySelectorAll('[data-btwg-error]').forEach((element) => {
        element.textContent = '';
      });
    };

    const validateWeight = (input, errorName, label) => {
      const rawValue = input.value.trim();
      const value = Number(rawValue);
      const isMetric = currentUnit === 'metric';
      const minimum = isMetric ? 32 : 70;
      const maximum = isMetric ? 227 : 500;

      if (!rawValue) {
        setError(errorName, `Please enter your ${label}.`);
        return false;
      }

      if (!Number.isFinite(value) || !/^\d+(?:\.\d)?$/.test(rawValue)) {
        setError(errorName, 'Please enter a valid value with no more than one decimal place.');
        return false;
      }

      if (value < minimum || value > maximum) {
        setError(errorName, `Value must be between ${minimum} and ${maximum}.`);
        return false;
      }

      setError(errorName, '');
      return true;
    };

    const validateHeight = () => {
      if (currentUnit === 'metric') {
        const rawValue = heightCmInput.value.trim();
        const value = Number(rawValue);

        if (!rawValue) {
          setError('height', 'Please enter your height.');
          return false;
        }

        if (!Number.isInteger(value) || value < 122 || value > 213) {
          setError('height', 'Height must be a whole number between 122 and 213 cm.');
          return false;
        }
      } else {
        const feet = Number(heightFeetInput.value);
        const inches = Number(heightInchesInput.value);

        if (heightFeetInput.value === '' || heightInchesInput.value === '') {
          setError('height', 'Please enter your height in feet and inches.');
          return false;
        }

        if (!Number.isInteger(feet) || feet < 4 || feet > 7 || !Number.isInteger(inches) || inches < 0 || inches > 11) {
          setError('height', 'Height must be 4-7 feet and 0-11 inches.');
          return false;
        }
      }

      setError('height', '');
      return true;
    };

    const validateWeek = () => {
      const value = Number(weekInput.value);

      if (!Number.isInteger(value) || value < 1 || value > 42) {
        setError('week', 'Pregnancy week must be a whole number between 1 and 42.');
        return false;
      }

      setError('week', '');
      return true;
    };

    const validateAll = () => {
      clearErrors();
      const validations = [
        validateWeight(preWeightInput, 'preWeight', 'pre-pregnancy weight'),
        validateWeight(currentWeightInput, 'currentWeight', 'current weight'),
        validateHeight(),
        validateWeek(),
      ];
      return validations.every(Boolean);
    };

    const convertHeightToMetric = () => {
      if (heightFeetInput.value === '' || heightInchesInput.value === '') return;
      const totalInches = Number(heightFeetInput.value) * 12 + Number(heightInchesInput.value);
      heightCmInput.value = String(Math.round(totalInches * 2.54));
    };

    const convertHeightToImperial = () => {
      if (heightCmInput.value === '') return;
      const roundedInches = Math.round(Number(heightCmInput.value) / 2.54);
      heightFeetInput.value = String(Math.floor(roundedInches / 12));
      heightInchesInput.value = String(roundedInches % 12);
    };

    const convertWeightInput = (input, toMetric) => {
      if (input.value === '') return;
      const value = Number(input.value);
      if (!Number.isFinite(value)) return;
      input.value = (toMetric ? value / POUNDS_PER_KILOGRAM : value * POUNDS_PER_KILOGRAM).toFixed(1);
    };

    const updateUnitUi = (convertValues = true) => {
      const nextUnit = unitInput.value;
      const toMetric = nextUnit === 'metric';

      if (convertValues && nextUnit !== currentUnit) {
        convertWeightInput(preWeightInput, toMetric);
        convertWeightInput(currentWeightInput, toMetric);
        if (toMetric) convertHeightToMetric();
        else convertHeightToImperial();
      }

      currentUnit = nextUnit;
      weightUnit.textContent = toMetric ? '(kg)' : '(lbs)';
      currentWeightUnit.textContent = toMetric ? '(kg)' : '(lbs)';
      metricHeight.hidden = !toMetric;
      imperialHeight.hidden = toMetric;
      preWeightInput.min = toMetric ? '32' : '70';
      preWeightInput.max = toMetric ? '227' : '500';
      currentWeightInput.min = toMetric ? '32' : '70';
      currentWeightInput.max = toMetric ? '227' : '500';
      preWeightInput.placeholder = toMetric ? 'Enter pre-pregnancy weight in kg' : 'Enter pre-pregnancy weight in lbs';
      currentWeightInput.placeholder = toMetric ? 'Enter current weight in kg' : 'Enter current weight in lbs';
      clearErrors();
    };

    const getWeightInPounds = (input) => {
      const value = Number(input.value);
      return currentUnit === 'metric' ? value * POUNDS_PER_KILOGRAM : value;
    };

    const getHeightInInches = () => {
      if (currentUnit === 'metric') return Number(heightCmInput.value) / 2.54;
      return Number(heightFeetInput.value) * 12 + Number(heightInchesInput.value);
    };

    const formatWeight = (weightInPounds) => {
      const unit = currentUnit === 'metric' ? 'kg' : 'lbs';
      const displayWeight = currentUnit === 'metric' ? weightInPounds / POUNDS_PER_KILOGRAM : weightInPounds;
      return `${displayWeight.toFixed(1)} ${unit}`;
    };

    const renderTips = (category) => {
      tipsList.replaceChildren();
      tipsByCategory[category].forEach((tip) => {
        const item = document.createElement('li');
        item.textContent = tip;
        tipsList.appendChild(item);
      });
    };

    const renderResult = () => {
      const preWeightInPounds = getWeightInPounds(preWeightInput);
      const currentWeightInPounds = getWeightInPounds(currentWeightInput);
      const heightInInches = getHeightInInches();
      const currentWeek = Number(weekInput.value);
      const pregnancyType = pregnancyTypeInput.value;
      const actualGain = currentWeightInPounds - preWeightInPounds;
      const bmi = (preWeightInPounds * 703) / (heightInInches * heightInInches);
      const category = getBmiCategory(bmi);
      const guidelines = pregnancyType === 'triplets'
        ? weightGainGuidelines.triplets.all
        : weightGainGuidelines[pregnancyType][category];

      const weeksPastFirstTrimester = Math.max(0, currentWeek - 12);
      const minimumExpectedGain = currentWeek <= 12
        ? FIRST_TRIMESTER_GAIN.min
        : END_OF_FIRST_TRIMESTER_GAIN.min + weeksPastFirstTrimester * guidelines.weekly.min;
      const maximumExpectedGain = currentWeek <= 12
        ? FIRST_TRIMESTER_GAIN.max
        : END_OF_FIRST_TRIMESTER_GAIN.max + weeksPastFirstTrimester * guidelines.weekly.max;

      let statusType = 'within';
      let statusMessage = 'Your weight gain is within the recommended range.';
      if (actualGain < minimumExpectedGain) {
        statusType = 'below';
        statusMessage = 'Your weight gain is below the recommended range for this stage of pregnancy.';
      } else if (actualGain > maximumExpectedGain) {
        statusType = 'above';
        statusMessage = 'Your weight gain is above the recommended range for this stage of pregnancy.';
      }

      const progressPercentage = Math.max(0, Math.min((actualGain / guidelines.total.max) * 100, 100));
      const totalRange = `${formatWeight(guidelines.total.min)} to ${formatWeight(guidelines.total.max)}`;
      const weeklyRange = currentWeek <= 12
        ? 'Weight gain varies during the first trimester'
        : `${formatWeight(guidelines.weekly.min)} to ${formatWeight(guidelines.weekly.max)}`;
      const expectedRange = `${formatWeight(minimumExpectedGain)} to ${formatWeight(maximumExpectedGain)}`;

      bmiElement.textContent = bmi.toFixed(1);
      categoryElement.textContent = capitalize(category);
      totalRangeElement.textContent = totalRange;
      actualGainElement.textContent = formatWeight(actualGain);
      statusElement.textContent = statusMessage;
      statusElement.className = `btwg__result-value btwg__status btwg__status--${statusType}`;
      weeklyRangeElement.textContent = weeklyRange;
      resultWeekElement.textContent = String(currentWeek);
      expectedRangeElement.textContent = expectedRange;
      progressFill.style.width = `${progressPercentage}%`;
      progressBar.setAttribute('aria-valuenow', String(Math.round(progressPercentage)));
      progressBar.setAttribute(
        'aria-valuetext',
        `${formatWeight(actualGain)} gained on a 0 to ${formatWeight(guidelines.total.max)} scale; recommended total ${totalRange}.`,
      );
      minWeightElement.textContent = formatWeight(0);
      maxWeightElement.textContent = formatWeight(guidelines.total.max);
      renderTips(category);

      latestResult = {
        measurementSystem: currentUnit,
        pregnancyType,
        pregnancyWeek: currentWeek,
        bmi: bmi.toFixed(1),
        bmiCategory: category,
        totalRecommendedGain: totalRange,
        currentWeightGain: formatWeight(actualGain),
        status: statusMessage,
        weeklyRecommendedGain: weeklyRange,
        expectedGain: expectedRange,
      };

      results.hidden = false;
      saveStatus.textContent = '';
      saveStatus.removeAttribute('data-state');
      results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!validateAll()) {
        root.querySelector('[data-btwg-error]:not(:empty)')?.previousElementSibling?.focus?.();
        return;
      }
      renderResult();
    });

    unitInput.addEventListener('change', () => {
      updateUnitUi(true);
      results.hidden = true;
      latestResult = null;
    });

    preWeightInput.addEventListener('blur', () => validateWeight(preWeightInput, 'preWeight', 'pre-pregnancy weight'));
    currentWeightInput.addEventListener('blur', () => validateWeight(currentWeightInput, 'currentWeight', 'current weight'));
    heightCmInput.addEventListener('blur', validateHeight);
    heightFeetInput.addEventListener('blur', validateHeight);
    heightInchesInput.addEventListener('blur', validateHeight);
    weekInput.addEventListener('blur', validateWeek);

    saveForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const email = emailInput.value.trim();
      saveStatus.textContent = '';
      saveStatus.removeAttribute('data-state');

      if (!email || !emailInput.checkValidity()) {
        saveStatus.textContent = 'Please enter a valid email address.';
        saveStatus.dataset.state = 'error';
        emailInput.focus();
        return;
      }

      if (!latestResult) {
        saveStatus.textContent = 'Calculate your result before saving it.';
        saveStatus.dataset.state = 'error';
        return;
      }

      try {
        saveLocally({ email, savedAt: new Date().toISOString(), ...latestResult });
        emailInput.value = '';
        saveStatus.textContent = 'Calculation saved successfully on this device.';
        saveStatus.dataset.state = 'success';
      } catch (error) {
        saveStatus.textContent = 'Your browser blocked local saving. Please take a screenshot of the result.';
        saveStatus.dataset.state = 'error';
      }
    });

    resetButton.addEventListener('click', () => {
      form.reset();
      currentUnit = 'imperial';
      updateUnitUi(false);
      clearErrors();
      results.hidden = true;
      latestResult = null;
      progressFill.style.width = '0%';
      progressBar.setAttribute('aria-valuenow', '0');
      progressBar.removeAttribute('aria-valuetext');
      saveForm.reset();
      saveStatus.textContent = '';
      saveStatus.removeAttribute('data-state');
      preWeightInput.focus();
    });

    copyButton.addEventListener('click', async () => {
      const code = copyButton.dataset.code || '';
      const originalLabel = `Code: ${code}`;

      try {
        await copyText(code);
        copyButton.textContent = 'Copied!';
      } catch (error) {
        copyButton.textContent = 'Copy failed';
      }

      window.setTimeout(() => {
        copyButton.textContent = originalLabel;
      }, 1200);
    });

    updateUnitUi(false);
  };

  const initAll = () => {
    document.querySelectorAll('[data-baby-weight-full]').forEach(initCalculator);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
